import { Buffer } from "buffer";
import { TQuery } from "jkurwa";

import {
  GATEWAY_DEFAULT_CONTENT_TYPE,
  GATEWAY_DEFAULT_TIMEOUT_MS,
  GATEWAY_TARGET_URL_HEADER,
} from "@prostocheck/shared";

import { isExtensionGateAvailable, requestViaExtensionGate } from "../extension-gate";
import { IGatewayRequest, IGatewayResponse } from "./gateway.models";

/**
 * Neither the ЦСК CMP servers nor the fiscal server send CORS headers, and the
 * CMP ones speak plain HTTP — a browser cannot call either directly. Two
 * transports exist for that reason, and every CMP fetch, TSP timestamp call
 * and fiscal command funnels through this one function, so the choice lives
 * here rather than at each caller:
 *
 * 1. The jkurwa-web gate browser extension, when installed — it makes the
 *    request from its own extension context, where neither restriction
 *    applies, and hands back the real response untouched.
 * 2. Otherwise, a forwarding gateway that passes the body through untouched
 *    and names the real target in a header.
 *
 * See ai/docs/kep-signing.md.
 */
export const gatewayRequest = async ({
  gatewayUrl,
  request,
}: {
  gatewayUrl: string;
  request: IGatewayRequest;
}): Promise<IGatewayResponse> => {
  const timeoutMs = request.timeoutMs ?? GATEWAY_DEFAULT_TIMEOUT_MS;
  const contentType = request.contentType ?? GATEWAY_DEFAULT_CONTENT_TYPE;

  if (isExtensionGateAvailable()) {
    const response = await requestViaExtensionGate({
      url: request.url,
      body: request.body,
      contentType,
      timeoutMs,
    });

    return { status: response.status, body: Buffer.from(response.body) };
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(gatewayUrl, {
      method: "POST",
      headers: {
        "Content-Type": contentType,
        [GATEWAY_TARGET_URL_HEADER]: request.url,
      },
      body: request.body as BodyInit,
      signal: controller.signal,
    });

    return {
      status: response.status,
      body: Buffer.from(await response.arrayBuffer()),
    };
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * The transport jkurwa expects: `query(method, url, headers, payload, cb)`.
 * The caller's `Content-Type` is forwarded — the TSP service sets
 * `application/tsp-request` and time stamping fails without it.
 */
export const createGatewayQuery = (gatewayUrl: string): TQuery => {
  const query: TQuery = (_method, url, headers, payload, callback) => {
    gatewayRequest({
      gatewayUrl,
      request: {
        url,
        body: payload ?? Buffer.alloc(0),
        contentType: headers?.["Content-Type"],
      },
    })
      .then(({ body, status }) => callback(body, status))
      .catch(() => callback(null, 0));
  };

  return query;
};
